import {
  EmailVerificationCommandResultStatus,
  type SignInResult,
  SignInStatus,
} from "Infrastructure/Api/Api";
import {
  type AppUser,
  ResetPasswordStep,
  SetPasswordStep,
  SignInStep,
} from "State/Auth/Models/AuthStateModels";
import {
  resetPasswordAsync,
  setPasswordResetStep,
} from "State/Auth/Passwords/ResetPasswordState";
import {
  setPasswordAsync,
  setPasswordSetStep,
} from "State/Auth/Passwords/SetPasswordsState";
import { signInSsoAsync } from "State/Auth/SignIn/SignInSsoState";
import { signInAsync } from "State/Auth/SignIn/SignInState";
import { signUpPasswordlessAsync } from "State/Auth/SignUp/SignUpPasswordlessState";
import { signUpAsync } from "State/Auth/SignUp/SignUpState";
import { getUserInfoAsync } from "State/Auth/UserInfo/GetUserInfoState";
import { verifyEmailAsync } from "State/Auth/Verifications/EmailVerificationState";
import type { sendPhoneVerificationTokenAsync } from "State/Auth/Verifications/SendPhoneVerificationTokenState";
import { verifyPhoneAsync } from "State/Auth/Verifications/VerifyPhoneState";
import { addMinutes, fromUnixTime } from "date-fns";
import { type ActionType, createAction, createReducer } from "typesafe-actions";

/**
 * Type of global state type, that handles "user"
 */
export type AuthState = {
  user: AppUser | null;
  errorCode: string | null;
  isLoading: boolean;
  ssoIsLoading: boolean;
  signOutDate: string | null;
  signInStep: SignInStep;
  resetPasswordStep: ResetPasswordStep;
  setPasswordStep: SetPasswordStep;
  phoneVerificationToken: string | null;
  unauthenticatedUrl: string | null;
  verificationEmail: string | null;
  isUserFetching?: boolean;
  isPasswordlessSign: boolean;
};

export const resetUser = createAction("@auth/RESET")<{
  onAfterReset?: () => void;
}>();

export const resetError = createAction("@auth/RESET_ERROR")<void>();

export const extendSignOutDate = createAction(
  "@auth/SET_SIGN_OUT_DATE",
)<void>();

export const setSignInStep = createAction(
  "@auth/SET_SIGN_IN_STEP",
)<SignInStep>();

export const setUnauthenticatedUrl = createAction(
  "@auth/SET_UNAUTHENTICATED_URL",
)<string | null>();

export const setVerificationEmail = createAction(
  "@auth/SET_VERIFICATION_EMAIL",
)<string | null>();

/**
 * Collection of actions, that can change state
 */
export type AuthAction =
  | ActionType<typeof signInAsync>
  | ActionType<typeof signUpAsync>
  | ActionType<typeof signUpPasswordlessAsync>
  | ActionType<typeof signInSsoAsync>
  | ActionType<typeof resetPasswordAsync>
  | ActionType<typeof setPasswordResetStep>
  | ActionType<typeof setPasswordSetStep>
  | ActionType<typeof setPasswordAsync>
  | ActionType<typeof sendPhoneVerificationTokenAsync>
  | ActionType<typeof verifyPhoneAsync>
  | ActionType<typeof verifyEmailAsync>
  | ActionType<typeof setUnauthenticatedUrl>
  | ActionType<typeof resetUser>
  | ActionType<typeof resetError>
  | ActionType<typeof setSignInStep>
  | ActionType<typeof extendSignOutDate>
  | ActionType<typeof setVerificationEmail>
  | ActionType<typeof getUserInfoAsync>;

/**
 * State is updated here.
 *
 * The most important rule of using Redux architecture is:
 *
 * NEVER MUTATE STATE !!!!!!!!!!!!!
 *
 * ALWAYS CREATE NEW OBJECT !!!
 */
export const authReducer = createReducer<AuthState, AuthAction>({
  user: null,
  errorCode: null,
  isLoading: false,
  ssoIsLoading: false,
  signOutDate: null,
  signInStep: SignInStep.Credentials,
  resetPasswordStep: ResetPasswordStep.Email,
  setPasswordStep: SetPasswordStep.Password,
  phoneVerificationToken: null,
  unauthenticatedUrl: null,
  verificationEmail: null,
  isPasswordlessSign: false,
})
  .handleAction(setVerificationEmail, (state, action) => {
    return { ...state, verificationEmail: action.payload, isLoading: false };
  })
  .handleAction(resetUser, (state) => {
    return { ...state, user: null, unauthenticatedUrl: null };
  })
  .handleAction(extendSignOutDate, (state) => {
    const signOutDate = addMinutes(new Date(), 24 * 60).toString();

    return {
      ...state,
      signOutDate,
    };
  })
  .handleAction(verifyPhoneAsync.failure, (state, action) => {
    return {
      ...state,
      errorCode: action.payload.message,
      isLoading: false,
    };
  })
  .handleAction(setUnauthenticatedUrl, (state, action) => {
    return {
      ...state,
      unauthenticatedUrl: action.payload,
    };
  })
  .handleAction(setSignInStep, (state, action) => {
    return {
      ...state,
      signInStep: action.payload,
      isLoading: false,
    };
  })
  .handleAction(signInAsync.success, (state, action) => {
    if (action.payload.status === SignInStatus.CodeVerificationStep) {
      return {
        ...state,
        errorCode: null,
        isLoading: false,
        signInStep: SignInStep.VerificationToken,
      };
    }

    if (action.payload.status === SignInStatus.Success) {
      return {
        ...state,
        user: mapSignInResultToAppUser(action.payload),
        errorCode: null,
        isLoading: false,
        signInStep: SignInStep.Credentials,
        isPasswordlessSign: action.payload.isPasswordlessSign ?? false,
      };
    }

    return {
      ...state,
      isLoading: false,
      errorCode: action.payload.errorMessage as string,
    };
  })
  .handleAction(signInAsync.request, (state) => {
    return { ...state, isLoading: true, errorCode: null };
  })
  .handleAction(signInAsync.failure, (state, action) => {
    return {
      ...state,
      errorCode: action.payload.message,
      isLoading: false,
    };
  })
  .handleAction(signInSsoAsync.request, (state) => {
    return { ...state, isLoading: true, ssoIsLoading: true };
  })
  .handleAction(signInSsoAsync.success, (state, action) => {
    if (action.payload.status === SignInStatus.Success) {
      return {
        ...state,
        user: mapSignInResultToAppUser(action.payload),
        errorCode: null,
        isLoading: false,
        ssoIsLoading: false,
        signInStep: SignInStep.Credentials,
      };
    }

    return {
      ...state,
      isLoading: false,
      errorCode: action.payload.errorMessage as string,
    };
  })

  .handleAction(signInSsoAsync.failure, (state, action) => {
    return {
      ...state,
      errorCode: action.payload.message,
      isLoading: false,
      ssoIsLoading: false,
    };
  })
  .handleAction(signUpAsync.success, (state, action) => {
    return {
      ...state,
      isLoading: false,
      verificationEmail: action.payload.login,
    };
  })
  .handleAction(signUpAsync.request, (state) => {
    return { ...state, isLoading: true };
  })
  .handleAction(signUpAsync.failure, (state, action) => {
    return {
      ...state,
      errorCode: action.payload.message,
      isLoading: false,
    };
  })
  .handleAction(signUpPasswordlessAsync.request, (state) => {
    return { ...state, isLoading: true };
  })
  .handleAction(signUpPasswordlessAsync.success, (state, _action) => {
    return {
      ...state,
      isLoading: false,
    };
  })
  .handleAction(signUpPasswordlessAsync.failure, (state, action) => {
    return {
      ...state,
      errorCode: action.payload.message,
      isLoading: false,
    };
  })
  .handleAction(verifyEmailAsync.success, (state, action) => {
    if (
      action.payload.status === EmailVerificationCommandResultStatus.Success &&
      !!action.payload.signInResult
    ) {
      return {
        ...state,
        user: mapSignInResultToAppUser(action.payload.signInResult),
        errorCode: null,
        isLoading: false,
      };
    }

    return {
      ...state,
      errorCode: "Error",
    };
  })
  .handleAction(resetPasswordAsync.request, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  })
  .handleAction(resetPasswordAsync.failure, (state) => {
    return {
      ...state,
      isLoading: false,
    };
  })
  .handleAction(resetPasswordAsync.success, (state) => {
    return {
      ...state,
      isLoading: false,
      resetPasswordStep: ResetPasswordStep.Success,
    };
  })
  .handleAction(setPasswordResetStep, (state, action) => {
    return {
      ...state,
      resetPasswordStep: action.payload,
    };
  })
  .handleAction(resetError, (state) => {
    return {
      ...state,
      errorCode: null,
    };
  })
  .handleAction(setPasswordAsync.success, (state, _action) => {
    return {
      ...state,
      setPasswordStep: SetPasswordStep.Success,
    };
  })
  .handleAction(setPasswordAsync.failure, (state, action) => {
    return {
      ...state,
      errorCode: action.payload.message,
    };
  })
  .handleAction(setPasswordSetStep, (state, action) => {
    return {
      ...state,
      setPasswordStep: action.payload,
    };
  })
  .handleAction(getUserInfoAsync.request, (state, action) => {
    if (action.payload?.withLoadingStatus) {
      return {
        ...state,
        isUserFetching: true,
      };
    }

    return state;
  })
  .handleAction(getUserInfoAsync.success, (state, action) => {
    return {
      ...state,
      user: {
        userStatus: action.payload.userStatus,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        accessRoleCode: action.payload.accessRoleCode ?? null,
        partyType: action.payload.partyType ?? null,
        accessRights: action.payload.accessRights,
        login: action.payload.login,
        isFirstSignIn: action.payload.isFirstSignIn,
        tokenExpiration: parseJwt(action.payload.token),
        profilePicture: action.payload.profilePicture ?? null,
        activeContextPublicID: action.payload.activeContextPublicID ?? null,
        userIdentityProviders: action.payload.userIdentityProviders,
        partyPublicID: action.payload.partyPublicID,
        userPublicID: action.payload.userPublicID,
        version: action.payload.version,
        userID: action.payload.userID,
        migrationRequired: action.payload.migrationRequired ?? false,
        regionRequired: action.payload.regionRequired ?? false,
        region: action.payload.region,
        handle: action.payload.handle,
        handleRequired: action.payload.handleRequired ?? false,
      },
      isUserFetching: false,
    };
  })
  .handleAction(getUserInfoAsync.failure, (state) => {
    return {
      ...state,
      isUserFetching: false,
    };
  });

const mapSignInResultToAppUser = (result: SignInResult) => {
  const user: AppUser = {
    userStatus: result.userStatus,
    login: result.login as string,
    firstName: result.firstName as string,
    lastName: result.lastName as string,
    accessRoleCode: result.accessRoleCode ?? null,
    partyType: result.partyType ?? null,
    accessRights: result.accessRights || [],
    isFirstSignIn: result.isFirstSignIn,
    tokenExpiration: parseJwt(result.token as string),
    userIdentityProviders: result.userIdentityProviders,
    profilePicture: result.profilePicture ?? null,
    activeContextPublicID: result.activeContextPublicID ?? null,
    partyPublicID: result.partyPublicID,
    userPublicID: result.userPublicID,
    version: result.version,
    userID: result.userID,
    migrationRequired: result.migrationRequired ?? false,
    regionRequired: result.regionRequired ?? false,
    region: result.region,
  };
  return user;
};

function parseJwt(token: string) {
  return fromUnixTime(
    JSON.parse(window.atob(token.split(".")[1])).exp,
  ).toISOString();
}
